import { BaseAction } from "../../../core/actions/BaseAction";
import { getAllOfferWithoutExchange, preferTrainTrips, tripsStillHeld } from "../../../Lib/Utils/booking";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IBookedOffer } from "../../../Orchestrator/Types/Index";
import { IGetBookingDetailStatusActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/BookingDetailActionParams";
import dayjs from "dayjs";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { AdmissionStatusEnum, BookingStatusEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { bookingsPastPivotDubai, loadBookingAppearanceMinutes } from "../../../Lib/Utils/bookingAppearancePeriod";

export class GetBookingDetailStatusAction extends BaseAction<IDefaultActionResult, IGetBookingDetailStatusActionParams> {

    override async run(): Promise<IDefaultActionResult> {
        const result: IDefaultActionResult = {
            data: null,
            message: "",
            status: "fail"
        }
        try {
            // Same reason as GetBookingDetailDataAction: the PAST stamp below
            // peeks the appearance period, and no caller upstream awaits it.
            await loadBookingAppearanceMinutes();

            const { booking } = this.params!;
            let bookingStatus: BookingStatusEnum | "" = ""
            if (booking) {
                const allBookedOfferWithoutExchange: IBookedOffer[] = getAllOfferWithoutExchange(booking?.booking?.bookedOffers)

                //Check booking is past booking
                //
                // TRIPS THIS BOOKING STILL TRAVELS, not `trips[0]`.
                //
                // An exchange leaves the trip it replaced on the booking, and on a
                // move to a LATER date it sits at `[0]`. Reading `[0]` therefore
                // stamped PAST off a journey the passenger no longer takes: booking
                // GEQIQ5, moved 06 Sep -> 11 Sep, hands this action BOTH trips and
                // `[0]` is the 06 Sep one, so the badge would flip to "Past" that
                // morning with the travel six days away. Seen on QGB756 and DKQGZ7,
                // whose badges read Past against live trips.
                //
                // `tripsStillHeld` is the LIST's own rule, reused rather than
                // rewritten. It keeps a trip when a covering offer still has a live
                // admission — the opposite polarity to "drop it if any admission is
                // superseded", and the difference is not academic: on a partial
                // exchange the original trip carries the mover's EXCHANGED admission
                // AND the stayer's FULFILLED one, and dropping it would strand the
                // passenger who never moved. It also sorts by `startTime`, and
                // carries the `admissions: []` vs `undefined` distinction that keeps
                // an ancillary-only offer from vouching for a trip it does not hold.
                //
                // An all-EXCHANGED booking with no replacement now yields no badge
                // at all rather than PAST — both `tripsStillHeld` passes reject it.
                // Judged unreachable (an exchange always mints a replacement).
                //
                // Its REFUNDED/RELEASED exclusion is right here even though
                // `cancelledLegTripIds` warns that `tripsStillHeld` is "the wrong
                // question for the DETAILS page": that warning is about RENDERING a
                // cancelled leg's card, not about deciding whether travel remains.
                // For "is it all done", a refunded leg is correctly excluded.
                //
                // ALIGNED with the list on pending exchanges, by using its helper:
                // `tripsStillHeld` counts EXCHANGE_ONGOING as still held, exactly as
                // the list does ("only EXCHANGED is gone"). Both trips therefore
                // survive a pending exchange and the later one decides — on the usual
                // move to a later date that is the replacement. Lenient is the right
                // side for a badge; nothing is given up until the exchange commits.
                //
                // The LAST remaining trip decides, not the first: a booking is past
                // only once every journey on it has gone. Reading the earliest left
                // the reported symptom reachable without any exchange at all — a
                // round trip whose outbound has flown said "Past" while the list
                // still showed the inbound under Upcoming, which is the same
                // list-vs-detail disagreement one shape over. (Narrower than it
                // sounds: the details page direction-SPLITS a round trip on this same
                // pivot, so the old rule usually saw one leg. The gap is the pre-split
                // window and the shapes that do not split.) This is also the
                // documented details-page semantic — see `hasEveryLegDeparted`, which
                // notes the details page uses the all-legs rule while the list uses
                // any-leg, "a live inbound legitimately keeps its actions after the
                // outbound has gone". `tripsStillHeld` sorts
                // by `startTime`, so this is the latest departure the passenger
                // still holds.
                // `preferTrainTrips` wraps it for the same reason every sibling that
                // reduces trips to a departure does (BookingOptions' hasDeparted,
                // GetBookingTripDetails, primaryTripForDirection — which records
                // omitting it as a shipped bug). The `fromStation` hop — the one that
                // runs AFTER the train, not the `toStation` feeder before it — sorts
                // last, so without this the badge would pivot on the bus and stay
                // Confirmed for an extra transfer window past the train, out of step
                // with `hasDeparted` two components over.
                //
                // This deliberately overrides the "every journey" rule above: a
                // booking is not still in progress because a feeder bus has not left,
                // and matching `hasDeparted`'s train-only scope is worth more than
                // literal completeness. Returns the whole list when nothing has a
                // train leg, so shuttle-only bookings decide on their shuttle.
                const statusTrips = preferTrainTrips(
                    tripsStillHeld(booking?.booking?.trips || [], booking?.booking?.bookedOffers || [])
                );

                if (statusTrips.length > 0) {
                    const tripStartDateTime = statusTrips[statusTrips.length - 1].startTime
                    // const givenDate = dayjs(tripStartDateTime).startOf('day');
                    // const currentDate = dayjs().startOf('day')          

                    //Convert tripStartDateTime Date to the Dubai time zone
                    const givenDate = dayjs(tripStartDateTime).tz("Asia/Dubai");
                    // Get current dubai time, less the appearance period: this
                    // stamps the booking PAST, and it must flip at the same
                    // instant the tabs do or the badge reads "past" on a booking
                    // the list is still showing under Upcoming.
                    const currentDate = bookingsPastPivotDubai()

                    //Check tripStartDateTime dat and current date as per dubai                                        
                    if (givenDate.isBefore(currentDate)) {
                        bookingStatus = BookingStatusEnum.PAST
                    }
                }
                //Check at least any one fulfilled
                if (bookingStatus === "") {
                    for (let i = 0; i < allBookedOfferWithoutExchange.length; i++) {
                        const bookSummaries = allBookedOfferWithoutExchange[i].admissions;
                        const isAnyFulfilled = bookSummaries.find(d => d.status === AdmissionStatusEnum.FULFILLED)
                        if (isAnyFulfilled) {
                            bookingStatus = BookingStatusEnum.FULFILLED
                            break;
                        }
                    }
                }

                //Check all are refunded.
                // if (bookingStatus === "") {
                //     for (let i = 0; i < allBookedOfferWithoutExchange.length; i++) {
                //         const bookSummaries = allBookedOfferWithoutExchange[i].admissions;
                //         const isAnyFulfilled = bookSummaries.find(d => d.status === "REFUNDED")
                //         if (isAnyFulfilled) {
                //             bookingStatus = "REFUNDED"
                //             break;
                //         }
                //     }
                // }
                const allRefunded = allBookedOfferWithoutExchange.length > 0 &&
                    allBookedOfferWithoutExchange.every(offer =>
                        offer.admissions.length > 0 &&
                        offer.admissions.every(admission => admission.status === AdmissionStatusEnum.REFUNDED || admission.status === AdmissionStatusEnum.RELEASED)
                    );
                if (allRefunded) {
                    bookingStatus = BookingStatusEnum.REFUNDED;
                }
            }
            updateRecoilState("bookingConfirmStatusAtom", bookingStatus)
            result.status = "success"
            result.data = bookingStatus
        } catch (error) {
            console.error("Error in GetBookingDetailStatusAction:", error);
        } finally {
            return result
        }
    }
}

// Create a singleton instance
export const getBookingDetailStatusAction = new GetBookingDetailStatusAction(); 